import type {
  RebatePoint,
  OverallRebateMetrics,
  RebateRuleSnapshot,
  RebateCategory,
} from "@erp/shared-types";

/**
 * Calculate overall rebate metrics from an array of RebatePoint records.
 * When isCumulative is true, uses the last point's cumulative values.
 * When isCumulative is false, sums all incremental values.
 *
 * IMPORTANT: Do NOT use breakdown record volumes for aggregation - they are n-counted
 * (duplicated across rules). Query RebateInvoiceLineItem table directly for accurate volumes.
 *
 * @param points - Array of RebatePoint records to aggregate
 * @param isCumulative - Whether to use cumulative values (from last point) or sum incrementals
 * @returns OverallRebateMetrics with totals and calculated percentage
 */
export function calculateOverallRebateMetrics(
  points: RebatePoint[],
  isCumulative: boolean = false,
): OverallRebateMetrics {
  if (points.length === 0) {
    return {
      totalRebateAmount: 0,
      totalVolume: 0,
      rebateableVolume: 0,
      affectedVolume: 0,
      rebatePercentage: 0,
    };
  }

  let totalRebateAmount: number;
  let totalVolume: number;
  let affectedVolume: number;

  if (isCumulative) {
    // Use cumulative values from the last point
    const lastPoint = points[points.length - 1];
    totalRebateAmount = lastPoint.totalCumulativeRebateAmount || 0;
    totalVolume = lastPoint.totalVolume || 0;
    affectedVolume = lastPoint.totalAffectedVolume || 0;
  } else {
    // Sum all incremental values
    const sums = points.reduce(
      (acc, p) => ({
        totalRebateAmount: acc.totalRebateAmount + p.totalRebateAmount,
        totalVolume: acc.totalVolume + (p.totalVolume || 0),
        affectedVolume: acc.affectedVolume + (p.totalAffectedVolume || 0),
      }),
      {
        totalRebateAmount: 0,
        totalVolume: 0,
        affectedVolume: 0,
      },
    );
    totalRebateAmount = sums.totalRebateAmount;
    totalVolume = sums.totalVolume;
    affectedVolume = sums.affectedVolume;
  }

  const rebatePercentage =
    totalVolume > 0 ? (totalRebateAmount / totalVolume) * 100 : 0;

  return {
    totalRebateAmount,
    totalVolume,
    rebateableVolume: affectedVolume, // Using affectedVolume as proxy for this utility
    affectedVolume,
    rebatePercentage,
  };
}

/**
 * Determine if a rebate rule is conditional based on the new definition.
 *
 * A rule is conditional if ANY of the following are true:
 * - thresholdType === 'growth'
 * - thresholdType === 'volume' AND tiers.length > 1
 * - thresholdType === 'volume' AND first tier threshold > $1
 *
 * Manual rules and flat volume rebates (single tier at $0 or $1) are non-conditional.
 *
 * @param rule - Rule object with thresholdType, isManual, and optional tiers
 * @returns true if the rule is conditional, false otherwise
 */
export function isConditionalRule(rule: {
  thresholdType: string | null;
  isManual: boolean;
  tiers?: Array<{ thresholdValue: string | number }>;
}): boolean {
  // Manual rules are always conditional (amounts are uncertain/one-time events)
  if (rule.isManual) return true;

  // Growth rules are always conditional
  if (rule.thresholdType === "growth") return true;

  // Volume rules: check tiers
  if (rule.thresholdType === "volume" && rule.tiers) {
    // Multiple tiers = conditional
    if (rule.tiers.length > 1) return true;

    // Single tier starting > $1 = conditional (accounts for $0 and $1 being "from dollar one")
    if (rule.tiers.length === 1) {
      const threshold =
        typeof rule.tiers[0].thresholdValue === "string"
          ? parseFloat(rule.tiers[0].thresholdValue)
          : rule.tiers[0].thresholdValue;
      if (threshold > 1) return true;
    }
  }

  // Flat volume starting at $0 or $1 = non-conditional
  return false;
}

/**
 * Determine whether a rule belongs to the primary "Redistribution" bucket.
 *
 * Single source of truth shared by the write path and the backfill so the two
 * always agree. When `primaryBucketingTagId` is provided the decision is
 * ID-based (immune to tag renames); otherwise it falls back to the legacy
 * substring match on tag names.
 */
export function tagsArePrimaryBucket(
  tags: Array<{ id: string; name?: string | null }> | undefined,
  primaryBucketingTagId?: string | null,
): boolean {
  if (!tags || tags.length === 0) return false;

  if (primaryBucketingTagId) {
    return tags.some((tag) => tag.id === primaryBucketingTagId);
  }

  // Legacy fallback: name-based substring match.
  return tags.some(
    (tag) => tag.name?.toLowerCase().includes("redist") ?? false,
  );
}

/**
 * Re-derive `isConditionalRule()` from a frozen breakdown snapshot.
 *
 * The snapshot stores `tierCount` + `firstTierThreshold`, which is exactly what
 * `isConditionalRule()` reads from `tiers` (its length and the first tier's
 * threshold), so we reconstruct a minimal tiers array and reuse that function —
 * no logic is duplicated.
 */
export function deriveIsConditionalFromSnapshot(
  snapshot: Pick<
    RebateRuleSnapshot,
    "thresholdType" | "isManual" | "tierCount" | "firstTierThreshold"
  >,
): boolean {
  const tiers = Array.from(
    { length: Math.max(0, snapshot.tierCount) },
    (_, i) => ({
      thresholdValue: i === 0 ? (snapshot.firstTierThreshold ?? 0) : 0,
    }),
  );

  return isConditionalRule({
    thresholdType: snapshot.thresholdType,
    isManual: snapshot.isManual,
    tiers,
  });
}

/**
 * Resolve the frozen categorization bucket from a breakdown snapshot.
 * Mirrors the precedence of the legacy live `categorizeRules()`:
 * primary bucket wins, then conditional, then non-conditional.
 */
export function bucketFromSnapshot(
  snapshot: RebateRuleSnapshot,
): RebateCategory {
  if (snapshot.isPrimaryBucket) return "Redistribution";
  if (deriveIsConditionalFromSnapshot(snapshot)) return "Conditional";
  return "Non-Conditional";
}

/**
 * Live (non-frozen) categorization of a rule — the counterpart to
 * `bucketFromSnapshot`, used for forecast rows and rows without a snapshot.
 *
 * Single source of truth for the live path so it can't drift from the frozen
 * one. The redistribution decision is ID-based via `tagsArePrimaryBucket`
 * (honouring the org's configured `primaryBucketingTagId`), preferring a
 * pre-resolved `rule.isPrimaryBucket` when the caller already computed it.
 */
export function categorizeRulesLive(
  rules: Array<{
    id: string;
    isConditional: boolean;
    isPrimaryBucket?: boolean;
    tags?: Array<{ id: string; name?: string | null }>;
  }>,
  primaryBucketingTagId?: string | null,
): Map<string, RebateCategory> {
  const map = new Map<string, RebateCategory>();
  for (const rule of rules) {
    if (map.has(rule.id)) continue; // first occurrence wins (merged rule lists)
    const isPrimary =
      rule.isPrimaryBucket ??
      tagsArePrimaryBucket(rule.tags, primaryBucketingTagId);
    map.set(
      rule.id,
      isPrimary
        ? "Redistribution"
        : rule.isConditional
          ? "Conditional"
          : "Non-Conditional",
    );
  }
  return map;
}
